import { Suspense, useEffect, useState } from 'react'
import { Outlet, Link, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import api from '../services/api'
import Loading from './Loading'
import MonitoringPortalIncoming from './MonitoringPortalIncoming'
import AdminPortalNavLinks from './AdminPortalNavLinks'
import { adminNavTo } from '../utils/mailAlertHost'
import { employeeHomeHref, isEmployeeHtmlDashboardPath } from '../utils/employeeDeskPath'
import EmployeeCabinetShell from './EmployeeCabinetShell'
import './Layout.css'

export default function Layout() {
  const { user, logout, isAdmin, canAccessTgMonitoring, canAccessFleetAiAnalytics, canAccessTaskBoardV2, canAccessCreativePortal } =
    useAuth()
  const location = useLocation()
  const path = location.pathname
  const employeeHomePath = employeeHomeHref(user)
  const employeeHomeNavUrl = adminNavTo(employeeHomePath)
  const employeeHomePathOnly = employeeHomeNavUrl.split('?')[0]
  const employeeHomeIsHtmlDash = isEmployeeHtmlDashboardPath(employeeHomePath)
  const normPos = String(user?.normalized_position || '').trim()
  const isSeoDeskContext = user?.role === 'employee' && (normPos === 'SEO' || path.startsWith('/employee/desk/seo/'))
  /** SEO: «Home» только дашборд /employee/desk/seo/…; иначе любой /employee/desk/* подсвечивал Home. */
  const employeeHomeActive = isSeoDeskContext
    ? path === employeeHomePathOnly || path.startsWith(`${employeeHomePathOnly}/`)
    : location.pathname.startsWith('/employee/desk') ||
        location.pathname === '/employee/home' ||
        location.pathname.startsWith('/employee/home/') ||
        location.pathname === '/assistant/home' ||
        location.pathname === '/seo_home' ||
        location.pathname.startsWith('/seo_home/')
  const monitoringWideLayout =
    path.startsWith('/admin/monitoring_tg') || path.startsWith('/employee/monitoring_tg')
  /** Портфельная ИИ: единый кабинет /employee/… или админка /admin/… */
  const employerSystemAi = path.startsWith('/employee/system-ai-analytics') || path.startsWith('/employer/system-ai-analytics')
  const adminSystemAi = path.startsWith('/admin/system-ai-analytics')
  const adminSystemAiPortal = Boolean(isAdmin && adminSystemAi)
  const adminSuiteLayout =
    path.startsWith('/meta-ai') ||
    path.startsWith('/google-ai') ||
    path.startsWith('/admin') ||
    (isAdmin && (path.startsWith('/facebook_api') || path.startsWith('/google_api'))) ||
    path.startsWith('/crm_api')
  const crcSpaPath = isAdmin ? '/admin/creative-calendar' : '/employee/creative-calendar'
  const systemAiHomeLike =
    employerSystemAi ||
    adminSystemAi ||
    path.startsWith('/admin/task-2-0') ||
    path.startsWith('/employee/tasks') ||
    path.startsWith('/employee/task-2-0') ||
    path.startsWith('/assistant/tasks') ||
    path.startsWith('/admin/creative-calendar') ||
    path.startsWith('/employee/creative-calendar')
  const taskBoardV2Wide =
    path.startsWith('/admin/task-2-0') ||
    path.startsWith('/employee/tasks') ||
    path.startsWith('/employee/task-2-0') ||
    path.startsWith('/assistant/tasks') ||
    path.startsWith('/admin/creative-calendar') ||
    path.startsWith('/employee/creative-calendar')
  const teamHubWide = path.startsWith('/admin/slack') || path.startsWith('/employee/slack')
  // The employee Slack route is authoritative. Role hydration may lag behind
  // the router for a moment, so tying this to user.role can briefly render the
  // outer cabinet and leave two sidebars on screen.
  const employeeTeamHub = path.startsWith('/employee/slack')

  // The signed-in employee always uses the settings-style cabinet, including
  // shared staff tools and SEO pages. Route permissions remain in ProtectedRoute.
  const isSeoEmployee = Boolean(isSeoDeskContext)
  const employeePortalLight = user?.role === 'employee'
  const employeeDeskCabinetMode = employeePortalLight

  const showAdminMobileDrawer = !employeeDeskCabinetMode && adminSuiteLayout && !monitoringWideLayout
  const [adminMobileNavOpen, setAdminMobileNavOpen] = useState(false)

  useEffect(() => {
    setAdminMobileNavOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!showAdminMobileDrawer || !adminMobileNavOpen) {
      document.documentElement.classList.remove('portal-nav-no-scroll')
      return
    }
    document.documentElement.classList.add('portal-nav-no-scroll')
    return () => document.documentElement.classList.remove('portal-nav-no-scroll')
  }, [showAdminMobileDrawer, adminMobileNavOpen])

  useEffect(() => {
    if (!adminMobileNavOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAdminMobileNavOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [adminMobileNavOpen])


  useEffect(() => {
    if (user?.role !== 'employee' || !user?.employee_id) return
    void api.post('/api/employee/portal-activity/ping').catch(() => {})
  }, [user?.role, user?.employee_id])

  useEffect(() => {
    if (employeePortalLight) {
      document.body.classList.add('employee-portal-light-body')
      if (employeeDeskCabinetMode) {
        document.body.classList.add('employee-portal-desk-unified')
      }
      return () => {
        document.body.classList.remove('employee-portal-light-body')
        document.body.classList.remove('employee-portal-desk-unified')
      }
    }
    document.body.classList.remove('employee-portal-desk-unified')
    return undefined
  }, [employeePortalLight, employeeDeskCabinetMode])

  useEffect(() => {
    if (adminSystemAiPortal) {
      document.body.classList.add('admin-portal-light-body')
      return () => document.body.classList.remove('admin-portal-light-body')
    }
    document.body.classList.remove('admin-portal-light-body')
    return undefined
  }, [adminSystemAiPortal])

  return (
    <div
      className={`layout${path.startsWith('/google-ai') ? ' layout--google-ai' : ''}${path.startsWith('/meta-ai') ? ' layout--meta-ai' : ''}${monitoringWideLayout ? ' layout--monitor-wide' : ''}${
        adminSuiteLayout && !employeeDeskCabinetMode ? ' layout--admin-suite' : ''
      }${systemAiHomeLike ? ' layout--system-ai-home' : ''}${
        adminSystemAiPortal ? ' layout--admin-system-ai' : ''
      }${
        taskBoardV2Wide ? ' layout--task-board-v2' : ''
      }${teamHubWide ? ' layout--team-hub' : ''}${
        employeePortalLight ? ' layout--employee-portal-light' : ''
      }${employeeDeskCabinetMode ? ' layout--employee-desk-cabinet' : ''}${
        employeeTeamHub ? ' layout--employee-team-hub' : ''
      }`}
    >
      {!employeeDeskCabinetMode && !employeeTeamHub ? (
      <nav
        className={`navbar${adminSuiteLayout ? ' navbar--admin-portal' : ''}${
          showAdminMobileDrawer && adminMobileNavOpen ? ' layout-admin-mobile-open portal-nav-drawer-open' : ''
        }`}
      >
        {showAdminMobileDrawer ? (
          <div
            className="layout-admin-nav-backdrop portal-nav-backdrop"
            data-portal-nav-backdrop
            aria-hidden="true"
            onClick={() => setAdminMobileNavOpen(false)}
          />
        ) : null}
        {adminSuiteLayout ? (
          <div className="nav nav--admin-full">
            <div className="nav-admin-primary">
              <div className="nav-admin-mobile-top">
                {isAdmin ? (
                  <a
                    href={adminNavTo('/home')}
                    className="nav-admin-logo"
                    aria-label="alex digital — home"
                  >
                    <span className="nav-admin-logo-text">alex digital</span>
                  </a>
                ) : employeeHomeIsHtmlDash ? (
                  <a
                    href={employeeHomeNavUrl}
                    className="nav-admin-logo"
                    aria-label="alex digital — home"
                  >
                    <span className="nav-admin-logo-text">alex digital</span>
                  </a>
                ) : (
                  <Link
                    to={employeeHomeNavUrl}
                    className="nav-admin-logo"
                    aria-label="alex digital — home"
                  >
                    <span className="nav-admin-logo-text">alex digital</span>
                  </Link>
                )}
                {showAdminMobileDrawer ? (
                  <button
                    type="button"
                    className="nav-admin-menu-toggle layout-admin-mobile-toggle"
                    data-portal-nav-toggle
                    aria-label="Открыть меню навигации"
                    aria-expanded={adminMobileNavOpen}
                    aria-controls="layout-nav-links"
                    onClick={() => setAdminMobileNavOpen((v) => !v)}
                  />
                ) : null}
              </div>
              <div
                id="layout-nav-links"
                className={`nav-admin-links nav-links${
                  showAdminMobileDrawer ? ' portal-mobile-drawer' : ''
                }`}
                data-portal-nav-drawer={showAdminMobileDrawer ? '' : undefined}
                role="navigation"
                onClick={(e) => {
                  if (!showAdminMobileDrawer || !adminMobileNavOpen) return
                  if ((e.target as HTMLElement).closest('a')) setAdminMobileNavOpen(false)
                }}
              >
                {isAdmin ? (
                  <AdminPortalNavLinks />
                ) : (
                  <>
                    {employeeHomeIsHtmlDash ? (
                      <a
                        href={employeeHomeNavUrl}
                        className={employeeHomeActive ? 'nav-link active' : 'nav-link'}
                      >
                        Home
                      </a>
                    ) : (
                      <Link
                        to={employeeHomeNavUrl}
                        className={employeeHomeActive ? 'nav-link active' : 'nav-link'}
                      >
                        Home
                      </Link>
                    )}
                    <Link
                      to={adminNavTo('/employee/slack')}
                      className={
                        location.pathname.startsWith('/employee/slack') ? 'nav-link active' : 'nav-link'
                      }
                    >
                      Slack
                    </Link>
                    {canAccessFleetAiAnalytics ? (
                      <Link
                        to={adminNavTo('/employee/system-ai-analytics')}
                        className={
                          location.pathname.startsWith('/employer/system-ai-analytics') ||
                          location.pathname.startsWith('/admin/system-ai-analytics')
                            ? 'nav-link active'
                            : 'nav-link'
                        }
                      >
                        AI analytics
                      </Link>
                    ) : null}
                    {canAccessTgMonitoring ? (
                      <Link
                        to={adminNavTo('/admin/monitoring_tg')}
                        className={
                          location.pathname.startsWith('/admin/monitoring_tg') ? 'nav-link active' : 'nav-link'
                        }
                      >
                        TG monitoring
                      </Link>
                    ) : null}
                    {canAccessCreativePortal ? (
                      <Link
                        to={adminNavTo(crcSpaPath)}
                        className={
                          location.pathname.startsWith('/admin/creative-calendar') ||
                          location.pathname.startsWith('/employee/creative-calendar')
                            ? 'nav-link active'
                            : 'nav-link'
                        }
                      >
                        КреоПортал
                      </Link>
                    ) : null}
                  </>
                )}
              </div>
            </div>
            <div className="nav-admin-aside">
              <div className="nav-user">
                <span>{user?.name || 'User'}</span>
                <button type="button" onClick={logout} className="btn btn-logout">
                  Log out
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="nav">
            {isSeoEmployee ? (
              <>
                <Link to={employeeHomeNavUrl} className={employeeHomeActive ? 'active' : ''}>
                  Home
                </Link>
                <Link
                  to={adminNavTo('/employee/tasks')}
                  className={location.pathname.startsWith('/employee/tasks') ? 'active' : ''}
                >
                  Tasks 2.0
                </Link>
                {canAccessCreativePortal ? (
                  <Link
                    to={adminNavTo(crcSpaPath)}
                    className={
                      location.pathname.startsWith('/admin/creative-calendar') ||
                      location.pathname.startsWith('/employee/creative-calendar')
                        ? 'active'
                        : ''
                    }
                  >
                    КреоПортал
                  </Link>
                ) : null}
                <a
                  href={adminNavTo('/seo')}
                  className={
                    location.pathname === '/seo' ||
                    (location.pathname.startsWith('/seo/') && !location.pathname.startsWith('/seo/table'))
                      ? 'active'
                      : ''
                  }
                >
                  SEO
                </a>
                <a
                  href={adminNavTo('/seo/table')}
                  className={
                    location.pathname === '/seo/table' || location.pathname.startsWith('/seo/table/')
                      ? 'active'
                      : ''
                  }
                >
                  TABLE
                </a>
              </>
            ) : (
              <>
                {employeeHomeIsHtmlDash ? (
                  <a href={employeeHomeNavUrl} className={employeeHomeActive ? 'active' : ''}>
                    Home
                  </a>
                ) : (
                  <Link to={employeeHomeNavUrl} className={employeeHomeActive ? 'active' : ''}>
                    Home
                  </Link>
                )}
                <Link
                  to={adminNavTo('/employee/tasks')}
                  className={location.pathname.startsWith('/employee/tasks') ? 'active' : ''}
                >
                  My tasks
                </Link>
                {canAccessCreativePortal ? (
                  <Link
                    to={adminNavTo(crcSpaPath)}
                    className={
                      location.pathname.startsWith('/admin/creative-calendar') ||
                      location.pathname.startsWith('/employee/creative-calendar')
                        ? 'active'
                        : ''
                    }
                  >
                    КреоПортал
                  </Link>
                ) : null}
              </>
            )}
            {!isAdmin ? (
              <Link
                to={adminNavTo('/employee/slack')}
                className={location.pathname.startsWith('/employee/slack') ? 'active' : ''}
              >
                Slack
              </Link>
            ) : null}
            {canAccessTaskBoardV2 && (
              <Link
                to={adminNavTo('/admin/task-2-0')}
                className={location.pathname.startsWith('/admin/task-2-0') ? 'active' : ''}
              >
                Task assignment
              </Link>
            )}
            {canAccessFleetAiAnalytics && (
              <Link
                to={adminNavTo(isAdmin ? '/admin/system-ai-analytics' : '/employee/system-ai-analytics')}
                className={
                  location.pathname.startsWith('/employer/system-ai-analytics') ||
                  location.pathname.startsWith('/admin/system-ai-analytics')
                    ? 'active'
                    : ''
                }
              >
                AI analytics
              </Link>
            )}
            {(isAdmin || canAccessTgMonitoring) && (
              <Link
                to={adminNavTo(isAdmin ? '/admin/monitoring_tg' : '/employee/monitoring_tg')}
                className={
                  location.pathname.startsWith('/admin/monitoring_tg') ||
                  location.pathname.startsWith('/employee/monitoring_tg')
                    ? 'active'
                    : ''
                }
              >
                TG monitoring
              </Link>
            )}
            <div className="nav-user">
              <span>{user?.name || 'User'}</span>
              <button type="button" onClick={logout} className="btn-logout">
                Log out
              </button>
            </div>
          </div>
        )}
      </nav>
      ) : null}
      <main className={`main-content${monitoringWideLayout ? ' main-content--monitor-wide' : ''}`}>
        {employeeDeskCabinetMode ? (
          <EmployeeCabinetShell>
            <Suspense fallback={<Loading />}><Outlet /></Suspense>
          </EmployeeCabinetShell>
        ) : (
          <Suspense fallback={<Loading />}><Outlet /></Suspense>
        )}
      </main>
      {canAccessTgMonitoring ? <MonitoringPortalIncoming /> : null}
    </div>
  )
}
